import get from 'lodash/get';

import { replaceWithLocalizedDateTimes } from 'web-services/utils/dates';
import { MESSAGE_TYPES } from 'web-ui-kit/messenger-consts';

import ConversationMember from './ConversationMember';
import Message from './Message';
import {
  SKIP_REASON_REMOVED,
  SKIP_REASON_SKIP_FOR_STAFF
} from './MessageFlowMessage';

const defaultProps = {
  /* The conversation UUID */
  id: '',
  /* The call history for the conversation */
  calls: [],
  /* Number of messages in the conversation */
  count: 0,
  /* The designer's userId for the space */
  designerId: -1,
  /* The bottom tat footer for the conversation */
  footer: '',
  /* Whether or not the currently logged in user is the project owner.
   */
  isProjectUser: false,
  /* Active conversation member info */
  members: {},
  /* Messages in the active conversation */
  messages: [],
  /* string used to query next page */
  page: null,
  /* Related spaceId for the conversation */
  spaceId: -1,
  /* The title for the conversation */
  spaceName: 'Conversation',
  /* The number of unread messages in the conversation by the current user */
  unreadMessageCount: 0
};

/** Helper function.
 * Maps the warehouse Product instance fields to our internal fields.
 */
const convertInternal = ({ loggedInUserId, spaceName, whMessages, whConversation }) => {
  convertKeysToCamelCase({ object: whMessages, replaces: MessageKeyReplaces });
  convertKeysToCamelCase({ object: whConversation, replaces: ConversationKeyReplaces });
  const members = generateMembersFromWarehouseConversation(whConversation);
  const designerId = getDesignerIdFromMembers(members);
  const projectUserId = Object.keys(members).find(memberId => members[memberId].role === 'user');
  const isProjectUser = Number(loggedInUserId) === Number(projectUserId);
  const updatedMessages = processMessages(whMessages.messages, designerId, isProjectUser).map(message =>
    Message.createFromWarehouseResponse({ whMessage: message, spaceId: message.spaceId })
  );

  // Replace localized times in the footer
  let footer = get(whConversation, 'footer', defaultProps.footer);
  if (footer) {
    footer = replaceWithLocalizedDateTimes(footer);
  }

  return {
    id: get(whConversation, 'conversationId', defaultProps.id),
    calls: get(whConversation, 'calls', defaultProps.calls),
    count: whMessages.count || defaultProps.count,
    designerId,
    footer,
    isProjectUser,
    members,
    messages: applyDatesAndStacking({ messages: updatedMessages }),
    page: whMessages.page || defaultProps.page,
    spaceId: get(whConversation, 'spaceId') || whMessages.spaceId || defaultProps.spaceId,
    spaceName: spaceName || defaultProps.spaceName,
    unreadMessageCount: get(whConversation, 'unreadMessageCount', defaultProps.unreadMessageCount)
  };
};

class Conversation {
  /**
   * Creates a new Conversation based on the server response
   * @argument whMessages: array server response from messages api
   * @argument whConversation: array server response from conversations api
   */
  static createFromWarehouseResponse ({ loggedInUserId, spaceName, whMessages, whConversation }) {
    return new Conversation({
      ...convertInternal({ loggedInUserId, spaceName, whMessages, whConversation })
    });
  }

  /** Creates a new Conversation
   * @argument fields: The attributes to add to this instance
   */
  constructor (fields = defaultProps) {
    Object.assign(this, fields);
  }

  /** Generates a copy of the Conversation instance
   * @argument overrides: The attributes to add to the new instance
  */
  makeCopy (overrides) {
    return new Conversation({
      id: this.id,
      calls: this.calls,
      count: this.count,
      designerId: this.designerId,
      footer: this.footer,
      isProjectUser: this.isProjectUser,
      members: { ...this.members },
      messages: [...this.messages],
      page: this.page,
      spaceId: this.spaceId,
      spaceName: this.spaceName,
      unreadMessageCount: this.unreadMessageCount,
      ...overrides
    });
  }

  makeCopyWithFreshConversationInfo ({ whConversation }) {
    if (Array.isArray(whConversation.members)) {
      whConversation.members = whConversation.members.reduce(reduceMembersToDict, {});
    }
    convertKeysToCamelCase({ object: whConversation, replaces: ConversationKeyReplaces });
    const members = generateMembersFromWarehouseConversation(whConversation);
    const designerId = getDesignerIdFromMembers(members);
    // Replace localized times in the footer
    let footer = get(whConversation, 'footer', defaultProps.footer);
    if (footer) {
      footer = replaceWithLocalizedDateTimes(footer);
    }
    return new Conversation({
      id: this.id,
      calls: get(whConversation, 'calls', this.calls),
      count: this.count,
      designerId: designerId,
      footer,
      isProjectUser: this.isProjectUser,
      members,
      messages: [...this.messages],
      page: this.page,
      spaceId: this.spaceId,
      spaceName: this.spaceName,
      unreadMessageCount: this.unreadMessageCount
    });
  }

  /**
   * Generates a new copy of the Conversation instance
   * deleting the last (active) message
   */
  makeCopyDeletingMessagesWithIds ({ messageIds }) {
    const newMessages = [...this.messages].filter(message => {
      for (const messageId of messageIds) {
        if (message.messageId.includes(messageId)) {
          return false;
        }
      }
      return true;
    });
    return new Conversation({
      id: this.id,
      calls: this.calls,
      count: this.count,
      designerId: this.designerId,
      footer: this.footer,
      isProjectUser: this.isProjectUser,
      members: { ...this.members },
      messages: applyDatesAndStacking({ messages: newMessages }),
      page: this.page,
      spaceId: this.spaceId,
      spaceName: this.spaceName,
      unreadMessageCount: this.unreadMessageCount
    });
  }

  /** Generates a new copy of the Conversation instance
   * replacing the automated message with the new message
   */
  makeCopyWithMessageReplace ({ whAutomatedMessageResponse, processWHMessages = true }) {
    convertKeysToCamelCase({ object: whAutomatedMessageResponse, replaces: MessageKeyReplaces });
    // Filter out all the client side generated messages
    const currentMessages = [...this.messages].filter(message =>
      !message.messageId.includes(whAutomatedMessageResponse.messageId) ||
      message.messageId === whAutomatedMessageResponse.messageId);
    // Generate new client side messages
    // if processMessages is false try and do an in-place replace
    const newMessages = processWHMessages
      ? processMessages([whAutomatedMessageResponse], this.designerId, this.isProjectUser).map(message =>
        Message.createFromWarehouseResponse({ whMessage: message, spaceId: message.spaceId })
      )
      : [whAutomatedMessageResponse];
    // Get index of current parent message
    const index = currentMessages.findIndex(message => message.messageId === whAutomatedMessageResponse.messageId);
    const messageIsRedesignRequest = get(whAutomatedMessageResponse, 'messageFlow.name', '') === 'redesign_request';
    if (messageIsRedesignRequest) {
      currentMessages.splice(index, 1);
      // put the message at the bottom
      currentMessages.push(...newMessages);
    } else {
      // Replace the message and add in the new messages
      currentMessages.splice(index, 1, ...newMessages);
    }
    return new Conversation({
      id: this.id,
      calls: this.calls,
      count: this.count,
      designerId: this.designerId,
      footer: this.footer,
      isProjectUser: this.isProjectUser,
      members: { ...this.members },
      messages: applyDatesAndStacking({ messages: currentMessages }),
      page: this.page,
      spaceId: this.spaceId,
      spaceName: this.spaceName,
      unreadMessageCount: this.unreadMessageCount
    });
  }

  /**
   * Generates a copy of the Conversation instance
   * appending a new messages to the beginning of the message list
   */
  makeCopyWithAdditionalLoadedMessages ({ whMessages }) {
    convertKeysToCamelCase({ object: whMessages, replaces: MessageKeyReplaces });
    const addedMessages = processMessages(whMessages.messages.reverse(), this.designerId, this.isProjectUser).map(message =>
      Message.createFromWarehouseResponse({ whMessage: message, spaceId: message.spaceId, animateIn: true })
    );
    const newMessages = [...addedMessages, ...this.messages];
    return new Conversation({
      id: this.id,
      calls: this.calls,
      count: this.count + whMessages.count,
      designerId: this.designerId,
      footer: this.footer,
      isProjectUser: this.isProjectUser,
      members: { ...this.members },
      messages: applyDatesAndStacking({ messages: newMessages }),
      page: whMessages.page,
      spaceId: this.spaceId,
      spaceName: this.spaceName,
      unreadMessageCount: this.unreadMessageCount
    });
  }

  /**
   * Generates a copy of the Conversation instance
   * appending a new message on to the end of the messages
   */
  makeCopyWithNewMessages ({ animateIn = true, whMessages }) {
    whMessages.forEach(whMessage => convertKeysToCamelCase({ object: whMessage, replaces: MessageKeyReplaces }));
    const addedMessages = processMessages(whMessages, this.designerId, this.isProjectUser).map(message =>
      Message.createFromWarehouseResponse({ whMessage: message, spaceId: message.spaceId, animateIn })
    );

    const newMessages = [...this.messages, ...addedMessages];
    return new Conversation({
      id: this.id,
      calls: this.calls,
      count: this.count,
      designerId: this.designerId,
      footer: this.footer,
      isProjectUser: this.isProjectUser,
      members: { ...this.members },
      messages: applyDatesAndStacking({ messages: newMessages }),
      page: this.page,
      spaceId: this.spaceId,
      spaceName: this.spaceName,
      unreadMessageCount: this.unreadMessageCount
    });
  }

  makeCopyWithTargetDesignSortedMessages () {
    const updatedMessages = [...this.messages];
    updatedMessages.forEach(message => {
      // If the message is a design selector for a redesign request we need to re-sort
      // the options by distance to the target design
      const selections = get(message, 'subComponentProps.selections');
      const messageNeedsSort = message.messageType === MESSAGE_TYPES.imageSelect &&
        selections && selections.length < 1 &&
        message.messageFlowName === 'redesign_request';
      if (messageNeedsSort) {
        message.subComponentProps.options = message.subComponentProps.options.sort(sortByDistanceToTargetDesign(Number.MAX_SAFE_INTEGER));
      }
    });
    return new Conversation({
      id: this.id,
      calls: this.calls,
      count: this.count,
      designerId: this.designerId,
      footer: this.footer,
      isProjectUser: this.isProjectUser,
      members: { ...this.members },
      messages: applyDatesAndStacking({ messages: updatedMessages }),
      page: this.page,
      spaceId: this.spaceId,
      spaceName: this.spaceName,
      unreadMessageCount: this.unreadMessageCount
    });
  }

  /**
   * @returns The conversation UUID
   */
  getId () {
    return this.id;
  }

  /**
   * @returns The active (latest) message for the conversation
   */
  getActiveMessage () {
    let activeMessage = null;
    if (this.messages.length > 0) {
      activeMessage = this.messages[this.messages.length - 1];
      if (activeMessage.parentMessageId) {
        activeMessage = this.getMessageById(activeMessage.parentMessageId);
      }
    }
    return activeMessage;
  }

  /**
   * Get active message type
   */
  getActiveMessageType () {
    let activeMessage = {};
    if (this.messages.length > 0) {
      activeMessage = this.messages[this.messages.length - 1];
    }
    return activeMessage.messageType;
  }

  /**
   * @returns The message with the message id
   */
  getMessageById (messageId) {
    return this.messages.find(message => message.messageId === messageId);
  }

  /**
   * @returns The active (latest) message's automated flow name or an empty string
   */
  getActiveMessageMessageFlowName () {
    const activeMessage = this.getActiveMessage() || {};
    return get(activeMessage, 'messageFlow.name') || get(activeMessage, 'messageFlowName', '');
  }

  /**
   * @returns the layout message in the conversation's messages
   */
  getActiveLayoutsMessage () {
    return this.messages.find(message => {
      return message.messageFlow && message.messageFlow.name === 'layout_selection';
    });
  }
}

/**
 * Defines keys we need to replace to support GraphQL expectations
 */
const MessageKeyReplaces = {
  image: 'imageSrc'
};

/**
 * Defines keys we need to replace for Conversations to support GraphQL expectations
 */
const ConversationKeyReplaces = {
  id: 'userId',
  image: 'avatarSrc'
};

const snakeToCamelCase = (str) => str.replace(
  /(_[a-z])/g,
  (group) => group.toUpperCase()
    .replace('_', '')
);

/**
 * Recursively replaces snake case keys to camel case keys
 */
const convertKeysToCamelCase = ({ object, replaces = {} }) => {
  if (!object) {
    return object;
  }
  if (Array.isArray(object)) {
    object.forEach(item => {
      convertKeysToCamelCase({ object: item, replaces });
    });
    return;
  }
  const keys = Object.keys(object);
  keys.forEach(key => {
    const oldObject = object[key];
    if (oldObject && typeof oldObject === 'object') {
      convertKeysToCamelCase({ object: oldObject, replaces });
    }
    let newKey = snakeToCamelCase(key);
    newKey = replaces[newKey] ? replaces[newKey] : newKey;
    if (newKey !== key) {
      delete Object.assign(object, { [newKey]: object[key] })[key];
    }
  });
};

/**
 * Copy value to label
 */
const copyValueToLabel = (option) => ({
  ...option,
  label: option.value
});

/**
 * Converts the raw JSON member info into ConversationMember objects
 * @param {*} whConversation Conversation Data coming from Warehouse
 */
const generateMembersFromWarehouseConversation = (whConversation) => {
  return (whConversation && Object.keys(whConversation.members).map(memberId =>
    ConversationMember.createFromWarehouseResponse({ whConversationMember: whConversation.members[memberId] })
  ).reduce(reduceMembersToDict, {})) || {};
};

/**
 * Gets the Designer Id from the conversation members
 * @param [ConversationMember] conversationMembers -
 * - The list of members that are in this conversation object
 */
const getDesignerIdFromMembers = (members) => {
  return Object.keys(members).find(memberId => members[memberId].role === 'designer');
};

/**
 * @returns Calculated props for designer card component
 */
const getPropsForDesignCard = ({ message }) => {
  const option = message.options.find(option => option.value === message.response[0]);
  return {
    comment: message.message,
    option: {
      ...option,
      imageSrc: `/${option.imageSrc}`
    },
    tag: 'Selected layout',
    useImgix: true
  };
};

/**
 * @returns Calculated props for designer card component
 */
const getPropsForDesignerCard = ({ message, designerId }) => {
  return {
    titleText: message.message,
    designerId
  };
};

/**
 * @returns Calculated props for links with images components
 */
const getPropsForImageLink = ({ message }) => {
  const thumbnailLink = message.links && message.links[0];
  return {
    option: {
      // The image comes from the first link that is an https link
      value: thumbnailLink,
      label: message.message,
      imageSrc: message.linkThumbnail
    },
    subtitle: thumbnailLink,
    useImgix: false
  };
};

const sortOptionsForImageSelect = (option1, option2) => {
  const numberMatch1 = option1.label.match(/(\d+)/);
  const numberMatch2 = option2.label.match(/(\d+)/);
  // If we can sort by the numbers in the label (such as designs or layouts)
  if (numberMatch1.length > 0 && numberMatch2.length > 0) {
    return numberMatch1[0] - numberMatch2[0];
  }
  // Sort by string alphabetically
  return option1.label.localeCompare(option2.label);
};

/**
 * @returns Calculated props for image select components
 */
const getPropsForImageSelect = ({ message, messageFlowName }) => {
  const response = get(message, 'response[0]');

  // Basic sort either alphabetically or by number
  let sortFunction = sortOptionsForImageSelect;

  // Sort by distance to target
  if (messageFlowName === 'redesign_request') {
    sortFunction = sortByDistanceToTargetDesign(Number.MAX_SAFE_INTEGER);
  }

  const options = message.options.map(option => ({
    ...option,
    imageSrc: `/${option.imageSrc}`
  })).sort(sortFunction);
  return {
    options: options,
    selection: response ? { value: response } : null
  };
};

/**
 * @returns Calculated props for files message
 */
const getPropsForFilesMessage = ({ message }) => {
  if (message.images && message.images.length > 0) {
    // Legacy images message
    return {
      images: message.images.map((imageUrl, index) => ({
        src: imageUrl.includes('blob') ? imageUrl : `/${imageUrl}`,
        alt: `Message Image ${index}`
      }))
    };
  } else {
    const images = [];
    const videos = [];
    const downloadables = [];
    message.files.forEach(file => {
      const mime = get(file, 'mimetype', '');
      const isPSD = mime.includes('psd') || mime.includes('photoshop');
      const isImage = mime.includes('image');
      const isVideo = mime.includes('video');
      const data = {
        alt: file.filename
      };
      switch (true) {
        case isImage && !isPSD:
          data.src = `/${file.s3Key}`;
          images.push(data);
          break;
        case isVideo:
          data.src = `/${file.s3Key}`;
          videos.push(data);
          break;
        default:
          data.src = `/${file.s3Key}`;
          downloadables.push(data);
          break;
      }
    });
    return {
      images,
      downloadables,
      videos
    };
  }
};

/**
 * @returns Calculated props for multi-select components
 */
const getPropsForMultiSelect = ({ message }) => {
  let responseSelections = get(message, 'response', []);
  const active = !responseSelections || responseSelections.length < 1;
  responseSelections = responseSelections.map((option) => mapOptionToValueLabel(option));
  return {
    active,
    options: message.options.map(copyValueToLabel),
    selections: responseSelections
  };
};

/**
 * @returns Calculated props for design rating component
 */
const getPropsForRating = ({ message }) => ({
  rating: parseInt(get(message, 'response[0]', '0'), 10)
});

/**
 * @returns Calculated props for redesign request summary components
 */
const getPropsForRedesignRequest = ({ message }) => {
  const responseComment = get(message, 'messageFlow.messages[5].response[0]', '');
  const responseDesign = get(message, 'messageFlow.messages[1].response[0]', '');
  const imageOptions = get(message, 'messageFlow.messages[1].options', []);
  const responseImage = imageOptions.find(option => option.value === responseDesign);
  const responseImageSrc = `/${get(responseImage, 'imageSrc', '')}`;
  const responseChanges = get(message, 'messageFlow.messages[4].response', []);
  const responseRating = get(message, 'messageFlow.messages[2].response[0]', '0');
  const responseSelectedReasons = get(message, 'messageFlow.messages[3].response', []);
  return {
    comment: responseComment,
    designName: `Design ${responseDesign}`,
    imageSrc: responseImageSrc,
    rating: responseRating,
    selectedReasons: responseSelectedReasons.map(mapOptionToValueLabel),
    changes: responseChanges.map(mapOptionToValueLabel)
  };
};

/**
 * @returns Calculated props for request revision component
 */
const getPropsForRequestRevision = ({ message }) => ({
  disabled: false,
  labelText: message.options[0].label
});

/**
 * @returns Calculated props for messages with a button that acts as an external link
 */
const getPropsForButtonWithLink = ({ message }) => {
  return {
    option: {
      ...get(message, 'options[0]', {})
    }
  };
};

/**
 * Generates the subcomponent props for a message given its message type
 * and active messenger state
 */
export const getSubComponentPropsForMessage = ({
  userId,
  message,
  messageFlowName
}) => {
  const addtlInfo = { index: message.messageOrder - 1 };
  let props = { addtlInfo };
  switch (message.messageType) {
    case MESSAGE_TYPES.imageWithTextCard: {
      if (message.linkThumbnail) {
        props = { ...props, ...getPropsForImageLink({ message }) };
      } else {
        props = { ...props, ...getPropsForDesignCard({ message }) };
      }
      break;
    }
    case MESSAGE_TYPES.designerCard: {
      props = { ...props, ...getPropsForDesignerCard({ message, designerId: userId }) };
      break;
    }
    case MESSAGE_TYPES.rating: {
      props = { ...props, ...getPropsForRating({ addtlInfo, message }) };
      break;
    }
    case MESSAGE_TYPES.multiSelect: {
      props = { ...props, ...getPropsForMultiSelect({ message }) };
      break;
    }
    case MESSAGE_TYPES.imageSelect: {
      const imageSelectProps = getPropsForImageSelect({ message, messageFlowName });
      props = { ...props, ...imageSelectProps };
      break;
    }
    case MESSAGE_TYPES.filesMessage: {
      props = getPropsForFilesMessage({ message });
      break;
    }
    case MESSAGE_TYPES.redesignRequest: {
      props = { ...props, ...getPropsForRedesignRequest({ message }) };
      break;
    }
    case MESSAGE_TYPES.requestRevision: {
      props = { ...props, ...getPropsForRequestRevision({ message }) };
      break;
    }
    case MESSAGE_TYPES.buttonWithLink: {
      props = { ...props, ...getPropsForButtonWithLink({ message }) };
      break;
    }
    default:
      break;
  }
  return props;
};

/**
 * Maps the single option value to an object with { label, value }
 */
const mapOptionToValueLabel = (option) => ({
  label: option,
  value: option
});

const generateMessageFlowMessages = ({
  designerId,
  isProjectUser,
  lastMessageCreatedAt,
  message,
  messages,
  messageFlowName
}) => {
  // We need to break out of this loop so choosing to go with a traditional loop
  const { conversationId, createdAt, messageId, messageFlow, spaceId, updatedAt, userId } = message;
  for (let i = 0; i < messageFlow.messages.length; i++) {
    const autoMessage = messageFlow.messages[i];
    const subComponentProps = getSubComponentPropsForMessage({
      userId: userId,
      message: autoMessage,
      messageFlowName: messageFlow.name
    });
    subComponentProps.messageIndex = i;
    subComponentProps.selections = autoMessage.response &&
      autoMessage.response.map(mapOptionToValueLabel);
    const { action, messageType, senderId, skipReason } = autoMessage;
    let { detailText } = autoMessage;
    if (messageFlow.name === 'redesign_request') {
      // Have to special case redesign request since this series of messages gets condensed
      detailText = '';
    }
    let { message } = autoMessage;
    if (messageType === MESSAGE_TYPES.designerCard) {
      message = '';
    }
    const newMessage = {
      action,
      conversationId,
      createdAt: lastMessageCreatedAt || createdAt,
      detailText,
      message,
      messageFlowName,
      messageId: `${messageId}-${i}`,
      messageType,
      parentMessageId: messageId,
      spaceId,
      subComponentProps,
      userId: (senderId || -1).toString(),
      updatedAt
    };

    // Check for skip. For example, rate design step should
    // be marked as skipped when designer is logged in.
    const shouldSkip = (
      (!isProjectUser &&
      skipReason === SKIP_REASON_SKIP_FOR_STAFF) ||
      skipReason === SKIP_REASON_REMOVED
    );
    if (shouldSkip) {
      continue;
    }

    messages.push(newMessage);
    if (autoMessage.messageType !== MESSAGE_TYPES.text &&
      (!autoMessage.response || autoMessage.response.length < 1)) {
      // We've reach the point where the user is currently at
      // in the flow
      break;
    }
  }
};

const getReplacement = ({ link, replacementText }) =>
  `<a href="${link}" target="_blank">${replacementText}</a>`;

/**
 * Creates a regex pattern to find links in a string
 */
const getLinkRegex = ({ link }) => {
  const escapedLink = link.replace(/([.?*+^$[\]\\(){}|-])/g, '\\$1');
  return new RegExp(`${escapedLink}`, 'g');
};

const replacerFunction = (replacement) => (match, offset, string) => {
  // If this is a markdown link do not replace with html
  if (offset > 1 && string[offset - 1] === '(' && string[offset - 2] === ']') {
    return match;
  }
  return replacement;
};

/**
 * Takes a message and converts the links into html links
 * @param message - the message object that we want to add the links into
 * @param links - the validated links attached to the message
 */
const makeLinks = ({ message }) => {
  const { links } = message;
  let messageText = message.message;
  links.forEach(link => {
    const linkWithoutProtocol = link.replace('https://', '')
      .replace('http://', '');
    const linkWithoutProtocolAndWWW = link.replace('https://www.', '')
      .replace('http://www.', '');
    if (messageText.includes(link)) {
      messageText = messageText.replace(
        getLinkRegex({ link }),
        replacerFunction(getReplacement({ link, replacementText: link }))
      );
    } else if (messageText.includes(linkWithoutProtocol)) {
      messageText = messageText.replace(
        getLinkRegex({ link: linkWithoutProtocol }),
        replacerFunction(getReplacement({ link, replacementText: linkWithoutProtocol }))
      );
    } else if (messageText.includes(linkWithoutProtocolAndWWW)) {
      messageText = messageText.replace(
        getLinkRegex({ link: linkWithoutProtocolAndWWW }),
        replacerFunction(getReplacement({ link, replacementText: linkWithoutProtocolAndWWW }))
      );
    }
  });
  return messageText;
};

/**
 * Splits out automated messages into separate message parts
 * @param newMessages: The new messages coming from the server
 *
 * @returns: New message array with split automated messages
 */
const processMessages = (newMessages, designerId, isProjectUser) => {
  let messages = [];
  let activeRedesignRequestMessage = null;
  let lastMessage = null;
  newMessages.forEach(message => {
    const isCondensedViewMessageFlow = message.messageFlow && message.messageFlow.name === 'redesign_request';
    if (message.messageFlow && (!message.messageFlow.complete || !isCondensedViewMessageFlow)) {
      if (message.messageFlow.name === 'redesign_request') {
        activeRedesignRequestMessage = message;
        return null;
      } else {
        // If this is an incomplete message flow add the messages for each step
        // that is complete up until where the user correctly is in the flow
        message.message ? messages.push(message) : messages.push({ ...message, hidden: true });
        generateMessageFlowMessages({
          isProjectUser,
          message,
          messages,
          messageFlowName: message.messageFlow.name
        });
      }
    } else if (message.messageFlow) {
      switch (message.messageFlow.name) {
        case 'redesign_request':
          message.messageType = MESSAGE_TYPES.redesignRequest;
          message.detailText = get(message, 'messageFlow.messages[5].detailText', '');
          message.subComponentProps = getSubComponentPropsForMessage({ message, userId: message.userId });
          break;
        default:
          break;
      }
      messages.push(message);
    } else if (message.linkThumbnail) {
      message.messageType = MESSAGE_TYPES.imageWithTextCard;
      message.subComponentProps = getSubComponentPropsForMessage({ message, userId: message.userId });
      message.message = '';
      messages.push(message);
    } else {
      if ((message.images && message.images.length > 0) || (message.files && message.files.length)) {
        const filesMessage = { ...message };
        filesMessage.messageType = MESSAGE_TYPES.filesMessage;
        filesMessage.subComponentProps = getSubComponentPropsForMessage({
          message: filesMessage,
          userId: message.userId
        });
        messages.push(filesMessage);
      }
      if (message.links && message.links.length > 0) {
        message.message = makeLinks({ message });
      }
      message.message && messages.push(message);
    }
    lastMessage = message;
  });

  // Remove any null messages
  messages.filter(message => !!message);
  // Shift the active redesign request message to always be on the bottom
  if (activeRedesignRequestMessage) {
    // If this is an incomplete message flow add the messages for each step
    // that is complete up until where the user correctly is in the flow
    activeRedesignRequestMessage.message
      ? messages.push(activeRedesignRequestMessage)
      : messages.push({ ...activeRedesignRequestMessage, hidden: true });
    // Make sure that the message createdAt is always at the bottom
    let createdAt = get(lastMessage, 'createdAt', activeRedesignRequestMessage.updatedAt);
    if (createdAt < activeRedesignRequestMessage.createdAt) {
      // Make sure that if the redesign request is the lastest message we use that
      // createdAt time
      createdAt = activeRedesignRequestMessage.createdAt;
    }
    generateMessageFlowMessages({
      isProjectUser,
      // We pass in the last message created at so that polling knows what time to use
      // If this is an update rather than initial pull, use the updatedAt value for the message
      lastMessageCreatedAt: get(lastMessage, 'createdAt', activeRedesignRequestMessage.updatedAt),
      message: activeRedesignRequestMessage,
      messages,
      messageFlowName: activeRedesignRequestMessage.messageFlow.name
    });
  }

  // Merge same tagged messages together
  // Temporarily store each tag in a dictionary
  // with the structure
  // {
  //   <tag>: first message encountered with this tag
  // }
  // when we find another message with the same tag
  // we merge the data together
  // for now this is only related to files so we
  // just merge the image/video/file data
  const tags = {};
  messages.forEach(message => {
    if (message.tag && !tags[message.tag] && message.subComponentProps) {
      // This is our primary message for this tag
      tags[message.tag] = message;
    } else if (message.tag && message.subComponentProps) {
      // Merge the file information to the first message
      const existingMessage = tags[message.tag];
      // Update the createdAt to the most recent message
      // this prevents issues with the same messages being polled again
      existingMessage.createdAt = message.createdAt;
      // Images
      const imagesPath = 'subComponentProps.images';
      existingMessage.subComponentProps.images = [
        ...get(existingMessage, imagesPath, []),
        ...get(message, imagesPath, [])
      ];
      // Videos
      const videosPath = 'subComponentProps.videos';
      existingMessage.videos = [
        ...get(existingMessage, videosPath, []),
        ...get(message, videosPath, [])
      ];
      // Downloadable files
      const downloadablesPath = 'subComponentProps.downloadables';
      existingMessage.downloadables = [
        ...get(existingMessage, downloadablesPath, []),
        ...get(message, downloadablesPath, [])
      ];
      message.markedForDelete = true;
    }
  });
  messages = messages.filter(message => !message.markedForDelete);

  return messages;
};

/**
 * Reduces conversation members to a dictionary with the
 * userId as the key
 */
export const reduceMembersToDict = (prev, member) => {
  const row = { [member.id]: member };
  return { ...prev, ...row };
};

/**
 * Sorts the options of a imageSelect message type by their relative distance to the target
 * design index
 */
const sortByDistanceToTargetDesign = (targetDesignIndex) => (option1, option2) => {
  const result1 = Math.abs(targetDesignIndex - parseInt(option1.value, 10));
  const result2 = Math.abs(targetDesignIndex - parseInt(option2.value, 10));
  return result1 - result2;
};

const datesAreSameDay = (date1, date2) =>
  date1 && date2 &&
  date1.getFullYear() === date2.getFullYear() &&
  date1.getMonth() === date2.getMonth() &&
  date1.getDate() === date2.getDate();

const createDateMessage = (date, dateString) => {
  return new Message({
    createdAt: date.toISOString(),
    createdAtDate: date,
    messageType: MESSAGE_TYPES.dateMessage,
    message: dateString,
    messageId: dateString,
    userId: -1
  });
};

/**
 * Determines whether two messages next to each other can be stacked
 *
 * Messages can be stacked if they come from the same user and do not have
 * an error, or if they do have an error, they must share the same parent
 * message id
 */
const messagesCanBeStacked = (message1, message2) => {
  if (!message1 || !message2) {
    return false;
  }
  // Can stack based on same user id
  const canBeStackedByUserId = message1.userId === message2.userId;
  // Can stack based on error state
  const canBeStackedWithError = !(message1.error || message2.error) ||
    (message1.parentMessageId === message2.parentMessageId);
  return canBeStackedByUserId && canBeStackedWithError;
};

/**
 * Apply Dates and Stacking Context
 * Loops through the messages, determines where date dividers
 * should go if there isn't one append it,
 * it also determines which messages should have the avatar and timestamp
 * @param messages: The list of messages to process
 * @returns new message list
 */
const applyDatesAndStacking = ({ messages }) => {
  let lastMessage = null;
  const options = { year: 'numeric', month: 'short', day: 'numeric' };
  const dateFormatter = new Intl.DateTimeFormat('en-US', options);
  const cleanMessages = messages.filter(message => message.messageType !== MESSAGE_TYPES.dateMessage);
  let newMessages = [];
  for (let i = 0; i < cleanMessages.length; i++) {
    cleanMessages[i].isStacked = false;
    const addDateMessage = !lastMessage || !datesAreSameDay(cleanMessages[i].createdAtDate, lastMessage.createdAtDate);
    addDateMessage && newMessages.push(createDateMessage(cleanMessages[i].createdAtDate, dateFormatter.format(cleanMessages[i].createdAtDate)));
    if (!addDateMessage && messagesCanBeStacked(cleanMessages[i], lastMessage)) {
      lastMessage.isStacked = true;
    }
    cleanMessages[i].message = replaceWithLocalizedDateTimes(cleanMessages[i].message);
    lastMessage = cleanMessages[i];
    newMessages.push(cleanMessages[i]);
  }
  return newMessages;
};

export default Conversation;
